import { Autocomplete, Stack, TextField } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { FilterSection } from 'v2/components/SearchAndFilters/SearchAndFilters';
import { TwoLineCell } from 'v2/components/TextCell/TextCell';
import { getBrowser12HourTime } from 'v2/utils/dateTime';
import { ACCESS_FILTER_ITEMS } from './eventFilterConstants';
import {
  EventDeviceOption,
  EventVisitorOption,
  getEventAccessLabel,
  getEventDetailOptions
} from './eventFilterOptions';
import { EventsFilterState } from './eventFilterTypes';

interface Params {
  translation: Translation;
  filters: EventsFilterState;
  visitorOptions: EventVisitorOption[];
  deviceOptions: EventDeviceOption[];
}

export const createEventFilterSections = ({
  translation,
  filters,
  visitorOptions,
  deviceOptions
}: Params): FilterSection[] => {
  const { access, detail, dateStart, dateEnd, user, device } = filters.filter;
  const {
    setFilterAccess,
    toggleFilterAccess,
    setFilterDetail,
    setFilterDateStart,
    setFilterDateEnd,
    clearFilterDates,
    setFilterUser,
    setFilterDevice
  } = filters.setters;
  const detailOptions = getEventDetailOptions(translation);
  const now = dayjs();

  return [
    {
      id: 'access',
      header: translation.access,
      type: 'radio',
      items: ACCESS_FILTER_ITEMS.map(({ key, value }) => ({
        key,
        label: getEventAccessLabel(value, translation),
        checked: access === value,
        onChange: () => toggleFilterAccess(value)
      })),
      isClearDisabled: access === null,
      onClear: () => setFilterAccess(null)
    },
    {
      id: 'date_and_time_range',
      header: translation.date_and_time_range,
      customContent: (
        <Stack spacing={2} py={2}>
          <DateTimePicker
            label={translation.start_date_time}
            value={dateStart}
            maxDateTime={now}
            ampm={getBrowser12HourTime()}
            closeOnSelect
            onChange={setFilterDateStart}
            slotProps={{ textField: { size: 'small', id: 'dateStart-input' } }}
          />
          <DateTimePicker
            label={translation.end_date_time}
            value={dateEnd}
            maxDateTime={now}
            ampm={getBrowser12HourTime()}
            closeOnSelect
            onChange={setFilterDateEnd}
            slotProps={{ textField: { size: 'small', id: 'dateEnd-input' } }}
          />
        </Stack>
      ),
      isClearDisabled: dateStart === null && dateEnd === null,
      onClear: clearFilterDates
    },
    {
      id: 'autocomplete_filters',
      customContent: (
        <Stack spacing={2} py={2}>
          <Autocomplete
            getOptionKey={(option) => option.id}
            disablePortal
            options={visitorOptions}
            value={visitorOptions.find((option) => option.id === user) ?? null}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} id="user-select" label={translation.all_users} />}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <TwoLineCell title={option.label} content={option.email} />
              </li>
            )}
            onChange={(_, newValue) => setFilterUser(newValue?.id ?? null)}
          />
          <Autocomplete
            disablePortal
            options={deviceOptions}
            value={deviceOptions.find((option) => option.id === device) ?? null}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} id="device-select" label={translation.all_devices} />}
            onChange={(_, newValue) => setFilterDevice(newValue?.id ?? null)}
          />
          <Autocomplete
            disablePortal
            options={detailOptions}
            getOptionLabel={(option) => option.label}
            value={detailOptions.find((option) => option.key === detail) ?? null}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            renderInput={(params) => <TextField {...params} id="details-select" label={translation.all_details} />}
            onChange={(_, newValue) => setFilterDetail(newValue?.key ?? null)}
          />
        </Stack>
      )
    }
  ];
};
